import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import jsQR from 'jsqr';
import { triggerHaptic } from '../utils/nativeBridge';
import { parseBazzefQrData } from '../utils/bazzefQr';
import { getMediaUrl } from '../utils/mediaUrl';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

/**
 * Real-time Bazzef QR Code Scanner with camera viewfinder, torch, gallery upload,
 * and 1-tap Message / Follow action sheet.
 */
const BazzefQrScanner = ({ isOpen, onClose, onUserScanned, onSwitchToCode }) => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const toast = useToast();

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(document.createElement('canvas'));
  const animFrameRef = useRef(null);
  const isScanningRef = useRef(false);

  const [facingMode, setFacingMode] = useState('environment');
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [isTorchOn, setIsTorchOn] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  // Scanned user state
  const [scannedResult, setScannedResult] = useState(null);
  const [scannedUser, setScannedUser] = useState(null);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  // Stop camera tracks
  const stopCamera = useCallback(() => {
    isScanningRef.current = false;
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsTorchOn(false);
  }, []);

  // Fetch scanned user data
  const handleScannedData = useCallback(
    async (rawText) => {
      const parsed = parseBazzefQrData(rawText);
      if (!parsed.isBazzef) {
        toast.error('Not a valid Bazzef QR code');
        resumeScanning();
        return;
      }

      triggerHaptic('success');
      setScannedResult(parsed);
      setIsLoadingUser(true);

      try {
        let endpoint = '';
        if (parsed.username) {
          endpoint = `/api/users/profile/${encodeURIComponent(parsed.username)}`;
        } else if (parsed.userId) {
          endpoint = `/api/users/${encodeURIComponent(parsed.userId)}`;
        } else {
          throw new Error('No user identifier in QR');
        }

        const res = await axios.get(endpoint);
        const userData = res.data?.user || res.data;
        setScannedUser(userData);
        setIsFollowing(Boolean(userData.isFollowing || userData.following));

        if (typeof onUserScanned === 'function') {
          onUserScanned(userData);
        }
      } catch (err) {
        toast.error('Could not find user associated with this QR code');
        resumeScanning();
      } finally {
        setIsLoadingUser(false);
      }
    },
    [toast, onUserScanned]
  );

  // Start continuous scanner loop
  const startScanningLoop = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    let barcodeDetector = null;
    if (typeof window !== 'undefined' && 'BarcodeDetector' in window) {
      try {
        barcodeDetector = new window.BarcodeDetector({ formats: ['qr_code'] });
      } catch (_) {}
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    const scanFrame = async () => {
      if (!isScanningRef.current || !video || video.readyState < 2) {
        if (isScanningRef.current) {
          animFrameRef.current = requestAnimationFrame(scanFrame);
        }
        return;
      }

      // 1. Hardware BarcodeDetector
      if (barcodeDetector) {
        try {
          const barcodes = await barcodeDetector.detect(video);
          if (barcodes.length > 0 && barcodes[0].rawValue) {
            isScanningRef.current = false;
            handleScannedData(barcodes[0].rawValue);
            return;
          }
        } catch (_) {}
      }

      // 2. jsQR Fallback
      if (ctx && video.videoWidth > 0 && video.videoHeight > 0) {
        // Downsample slightly for ultra-fast 60 FPS scanning
        const w = Math.min(video.videoWidth, 640);
        const h = Math.min(video.videoHeight, Math.round(640 * (video.videoHeight / video.videoWidth)));
        canvas.width = w;
        canvas.height = h;
        ctx.drawImage(video, 0, 0, w, h);

        const imgData = ctx.getImageData(0, 0, w, h);
        const code = jsQR(imgData.data, imgData.width, imgData.height, {
          inversionAttempts: 'dontInvert',
        });

        if (code && code.data) {
          isScanningRef.current = false;
          handleScannedData(code.data);
          return;
        }
      }

      if (isScanningRef.current) {
        animFrameRef.current = requestAnimationFrame(scanFrame);
      }
    };

    isScanningRef.current = true;
    animFrameRef.current = requestAnimationFrame(scanFrame);
  }, [handleScannedData]);

  // Start camera
  const startCamera = useCallback(
    async (mode) => {
      stopCamera();
      setCameraError(null);

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: mode,
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }

        // Check torch capability
        const track = stream.getVideoTracks()[0];
        if (track && typeof track.getCapabilities === 'function') {
          const caps = track.getCapabilities();
          setTorchAvailable(Boolean(caps.torch));
        }

        startScanningLoop();
      } catch (err) {
        console.warn('Camera error:', err);
        setCameraError('Camera access was denied or is unavailable on this device.');
      }
    },
    [stopCamera, startScanningLoop]
  );

  useEffect(() => {
    if (isOpen) {
      setScannedResult(null);
      setScannedUser(null);
      startCamera(facingMode);
    } else {
      stopCamera();
    }
    return () => {
      stopCamera();
    };
  }, [isOpen, facingMode, startCamera, stopCamera]);

  // Toggle Torch
  const toggleTorch = async () => {
    if (!streamRef.current) return;
    const track = streamRef.current.getVideoTracks()[0];
    if (track) {
      try {
        const nextState = !isTorchOn;
        await track.applyConstraints({
          advanced: [{ torch: nextState }],
        });
        setIsTorchOn(nextState);
        triggerHaptic('light');
      } catch (_) {}
    }
  };

  // Flip Camera
  const toggleFacingMode = () => {
    triggerHaptic('light');
    setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
  };

  // Resume Scanning
  const resumeScanning = () => {
    setScannedResult(null);
    setScannedUser(null);
    startScanningLoop();
  };

  // Pick Image from Gallery to decode
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const img = new Image();
    const reader = new FileReader();

    reader.onload = (event) => {
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);

        const imgData = ctx.getImageData(0, 0, img.width, img.height);
        const code = jsQR(imgData.data, imgData.width, imgData.height);

        if (code && code.data) {
          handleScannedData(code.data);
        } else {
          toast.error('No Bazzef QR code found in selected photo');
        }
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // 1-Tap Action: Direct Message
  const handleSendMessage = () => {
    if (!scannedUser) return;
    triggerHaptic('medium');
    onClose();

    navigate(`/messages/${scannedUser.id}`, {
      state: {
        openUser: {
          id: scannedUser.id,
          username: scannedUser.username,
          handle: scannedUser.handle || scannedUser.username,
          profilePicture: scannedUser.profilePicture || scannedUser.profile_picture,
          isVerified: scannedUser.isVerified,
        },
      },
    });
  };

  // 1-Tap Action: Follow / Unfollow
  const handleToggleFollow = async () => {
    if (!scannedUser) return;
    triggerHaptic('light');
    setFollowLoading(true);

    try {
      const endpoint = isFollowing
        ? `/api/users/unfollow/${scannedUser.id}`
        : `/api/users/follow/${scannedUser.id}`;
      await axios.post(endpoint);
      setIsFollowing(!isFollowing);
      toast.success(isFollowing ? `Unfollowed @${scannedUser.username}` : `Following @${scannedUser.username}!`);
    } catch (err) {
      toast.error('Could not update follow status');
    } finally {
      setFollowLoading(false);
    }
  };

  // View Profile
  const handleViewProfile = () => {
    if (!scannedUser) return;
    triggerHaptic('light');
    onClose();
    navigate(`/profile/${scannedUser.username || scannedUser.handle}`);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col justify-between overflow-hidden animate-in fade-in duration-200 select-none">
      {/* Top Controls Header */}
      <div className="relative z-20 flex items-center justify-between p-4 pt-[calc(1rem+env(safe-area-inset-top,0px))] bg-gradient-to-b from-black/80 to-transparent">
        <button
          type="button"
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur-md transition-all active:scale-95"
          aria-label="Close scanner"
        >
          <i className="fas fa-times text-lg" />
        </button>

        {onSwitchToCode ? (
          <div className="flex items-center p-1 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-xs font-bold">
            <button
              type="button"
              onClick={() => {
                triggerHaptic('light');
                onSwitchToCode();
              }}
              className="px-3.5 py-1.5 rounded-full text-white/80 hover:text-white transition-all flex items-center gap-1.5 cursor-pointer active:scale-95"
            >
              <i className="fas fa-qrcode text-xs" />
              <span>My Code</span>
            </button>
            <button
              type="button"
              className="px-3.5 py-1.5 rounded-full bg-white text-zinc-950 shadow-sm transition-all flex items-center gap-1.5 font-bold"
            >
              <i className="fas fa-camera text-[10px]" />
              <span>Scan</span>
            </button>
          </div>
        ) : (
          <span className="text-white font-bold text-sm tracking-wide bg-white/10 backdrop-blur-md px-3.5 py-1.5 rounded-full border border-white/15">
            Scan Bazzef Code
          </span>
        )}

        <div className="flex items-center gap-2">
          {torchAvailable && (
            <button
              type="button"
              onClick={toggleTorch}
              className={`w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-md transition-all active:scale-95 ${
                isTorchOn
                  ? 'bg-amber-400 text-black shadow-[0_0_15px_rgba(251,191,36,0.6)]'
                  : 'bg-white/10 hover:bg-white/20 text-white'
              }`}
              title="Torch"
              aria-label="Toggle Torch"
            >
              <i className="fas fa-bolt text-sm" />
            </button>
          )}

          <button
            type="button"
            onClick={toggleFacingMode}
            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur-md transition-all active:scale-95"
            title="Flip camera"
            aria-label="Flip camera"
          >
            <i className="fas fa-camera-rotate text-sm" />
          </button>
        </div>
      </div>

      {/* Main Viewfinder Area */}
      <div className="relative flex-1 flex items-center justify-center overflow-hidden">
        {cameraError ? (
          <div className="p-6 text-center text-rose-300 max-w-sm mx-auto">
            <i className="fas fa-video-slash text-4xl mb-3 opacity-80" />
            <p className="text-sm font-medium">{cameraError}</p>
          </div>
        ) : (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={`absolute inset-0 w-full h-full object-cover ${
              facingMode === 'user' ? 'scale-x-[-1]' : ''
            }`}
          />
        )}

        {/* Viewfinder Target Frame & Laser */}
        {!scannedUser && !isLoadingUser && (
          <div className="relative z-10 w-64 h-64 sm:w-72 sm:h-72 pointer-events-none flex items-center justify-center">
            {/* Corner Framing Brackets */}
            <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-indigo-400 rounded-tl-xl shadow-[0_0_10px_rgba(99,102,241,0.5)]" />
            <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-indigo-400 rounded-tr-xl shadow-[0_0_10px_rgba(99,102,241,0.5)]" />
            <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-indigo-400 rounded-bl-xl shadow-[0_0_10px_rgba(99,102,241,0.5)]" />
            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-indigo-400 rounded-br-xl shadow-[0_0_10px_rgba(99,102,241,0.5)]" />

            {/* Glowing Laser Scanner Line */}
            <div className="w-full h-0.5 bg-gradient-to-r from-transparent via-cyan-400 to-transparent shadow-[0_0_15px_#22d3ee] animate-pulse" />
          </div>
        )}

        {/* Loading Spinner when resolving user */}
        {isLoadingUser && (
          <div className="relative z-20 bg-black/70 backdrop-blur-md px-6 py-4 rounded-2xl border border-white/15 flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-white text-xs font-medium">Resolving Bazzef profile...</span>
          </div>
        )}
      </div>

      {/* Bottom Action Footer */}
      {!scannedUser && (
        <div className="relative z-20 p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))] bg-gradient-to-t from-black/90 to-transparent flex flex-col items-center gap-3">
          <p className="text-white/70 text-xs text-center max-w-xs font-medium">
            Point camera at a Bazzef QR code to instantly start a message or follow.
          </p>

          <div className="flex items-center gap-2.5">
            {onSwitchToCode && (
              <button
                type="button"
                onClick={() => {
                  triggerHaptic('light');
                  onSwitchToCode();
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white text-xs font-bold shadow-lg shadow-indigo-500/30 border border-white/20 backdrop-blur-md cursor-pointer transition-all active:scale-95"
              >
                <i className="fas fa-qrcode text-sm" />
                <span>Show My QR Code</span>
              </button>
            )}

            <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-bold border border-white/15 backdrop-blur-md cursor-pointer transition-all active:scale-95">
              <i className="far fa-images text-sm text-cyan-400" />
              <span>Upload Photo</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </label>
          </div>
        </div>
      )}

      {/* Scanned User Interactive Action Sheet */}
      {scannedUser && (
        <div className="relative z-30 p-5 pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))] bg-zinc-900 border-t border-white/15 rounded-t-3xl shadow-2xl animate-in slide-in-from-bottom duration-250 flex flex-col gap-4">
          <div className="w-10 h-1 bg-white/20 rounded-full mx-auto" />

          {/* User Profile Overview */}
          <div className="flex items-center gap-3.5">
            {scannedUser.profilePicture || scannedUser.profile_picture ? (
              <img
                src={getMediaUrl(scannedUser.profilePicture || scannedUser.profile_picture)}
                alt={scannedUser.username}
                className="w-14 h-14 rounded-full object-cover border-2 border-indigo-500 ring-2 ring-indigo-500/30"
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-gradient-to-tr from-indigo-600 to-violet-600 text-white font-bold text-xl flex items-center justify-center border-2 border-indigo-400">
                {scannedUser.username?.charAt(0).toUpperCase() || 'U'}
              </div>
            )}

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <h3 className="text-white font-extrabold text-base truncate">
                  {scannedUser.username}
                </h3>
                {scannedUser.isVerified && (
                  <i className="fas fa-circle-check text-sky-400 text-xs" />
                )}
              </div>
              <p className="text-zinc-400 text-xs truncate">
                @{scannedUser.handle || scannedUser.username}
              </p>
              {scannedUser.bio && (
                <p className="text-zinc-300 text-xs line-clamp-1 mt-0.5">
                  {scannedUser.bio}
                </p>
              )}
            </div>
          </div>

          {/* Primary Action Buttons: Send Message & Follow */}
          <div className="grid grid-cols-2 gap-2.5 pt-1">
            <button
              type="button"
              onClick={handleSendMessage}
              className="h-11 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white font-bold text-sm flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/30 active:scale-98 transition-all"
            >
              <i className="far fa-paper-plane text-sm" />
              <span>Message</span>
            </button>

            {currentUser?.id !== scannedUser.id && (
              <button
                type="button"
                onClick={handleToggleFollow}
                disabled={followLoading}
                className={`h-11 rounded-xl font-bold text-sm flex items-center justify-center gap-2 border transition-all active:scale-98 ${
                  isFollowing
                    ? 'bg-zinc-800 border-zinc-700 text-white hover:border-rose-500 hover:text-rose-400'
                    : 'bg-white text-zinc-950 border-white hover:bg-zinc-100 shadow-md'
                }`}
              >
                <i className={`fas ${isFollowing ? 'fa-user-check' : 'fa-user-plus'} text-xs`} />
                <span>{isFollowing ? 'Following' : 'Follow'}</span>
              </button>
            )}

            {currentUser?.id === scannedUser.id && (
              <button
                type="button"
                onClick={handleViewProfile}
                className="h-11 rounded-xl bg-zinc-800 text-white border border-zinc-700 font-bold text-sm flex items-center justify-center gap-2"
              >
                <span>Your Profile</span>
              </button>
            )}
          </div>

          {/* Secondary Options */}
          <div className="flex items-center justify-between text-xs text-zinc-400 pt-1 px-1">
            <button
              type="button"
              onClick={handleViewProfile}
              className="hover:text-white transition-colors"
            >
              View Full Profile →
            </button>
            <button
              type="button"
              onClick={resumeScanning}
              className="hover:text-indigo-400 transition-colors flex items-center gap-1"
            >
              <i className="fas fa-arrows-rotate text-[11px]" />
              <span>Scan Another</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(BazzefQrScanner);
